import type { JSX } from 'react';

export type NavIconName =
  | 'home'
  | 'learn'
  | 'class'
  | 'progress'
  | 'students'
  | 'tutor'
  | 'student'
  | 'sync'
  | 'activity'
  | 'worksheet'
  | 'parent'
  | 'game'
  | 'report'
  | 'lesson'
  | 'help'
  | 'info'
  | 'menu'
  | 'logout';

export interface NavIconProps {
  icon: NavIconName;
  size?: number;
  title?: string;
}

const PATHS: Record<NavIconName, string[]> = {
  home: ['M3 10.5 12 3l9 7.5', 'M5.5 9.5V21h13V9.5', 'M9.5 21v-6h5v6'],
  learn: [
    'M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23.5Z',
    'M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5a3.5 3.5 0 0 1 3.5 3.5Z',
  ],
  class: ['M4 20V7l8-4 8 4v13', 'M8 20v-6h8v6', 'M2 20h20'],
  progress: ['M5 19V9', 'M12 19V5', 'M19 19v-7', 'M3 19h18'],
  students: [
    'M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M2 21a6 6 0 0 1 12 0',
    'M17 11a3 3 0 1 0 0-6',
    'M16 15a5 5 0 0 1 6 5',
  ],
  tutor: [
    'M12 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M4.5 20.5a7.5 7.5 0 0 1 15 0',
    'M7.5 7l4.5-2.5L16.5 7 12 8.5z',
  ],
  student: [
    'M12 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M5.5 20.5a6.5 6.5 0 0 1 13 0',
    'M8.5 14v4.5',
    'M15.5 14v4.5',
    'M18 16l3-2',
  ],
  sync: ['M20 7h-5V2', 'M20 7a8 8 0 0 0-14-2', 'M4 17h5v5', 'M4 17a8 8 0 0 0 14 2'],
  activity: ['M4 12h4l2-6 4 12 2-6h4'],
  worksheet: [
    'M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z',
    'M15 3v5h5',
    'M8 13h8',
    'M8 17h6',
    'M8 9h4',
  ],
  parent: [
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M17 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M2.5 20a6.5 6.5 0 0 1 13 0',
    'M14.5 19a4.5 4.5 0 0 1 7 3.5',
  ],
  game: [
    'M6 8h12a4 4 0 0 1 4 4v3a3 3 0 0 1-3 3 3 3 0 0 1-2.6-1.5l-1-1.7a1 1 0 0 0-.86-.5H9.46a1 1 0 0 0-.86.5l-1 1.7A3 3 0 0 1 5 18a3 3 0 0 1-3-3v-3a4 4 0 0 1 4-4Z',
    'M8 12h-1',
    'M9 11v1',
    'M16 12h.01',
    'M17 11v.01',
  ],
  report: [
    'M7 4h7l5 5v11a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z',
    'M14 4v5h5',
    'M9 14h6',
    'M9 17h4',
  ],
  lesson: [
    'M4 6l1.5 1.5 3-3',
    'M11 7h9',
    'M4 12l1.5 1.5 3-3',
    'M11 13h9',
    'M4 18l1.5 1.5 3-3',
    'M11 19h6',
  ],
  help: [
    'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
    'M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5',
    'M12 17.5v.01',
  ],
  info: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 11v5', 'M12 8v.01'],
  menu: ['M4 6h16', 'M4 12h16', 'M4 18h16'],
  logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
};

export function NavIcon({ icon, size = 24, title }: NavIconProps): JSX.Element {
  const stroke = PATHS[icon] ?? [];
  const label = title ?? icon;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{label}</title> : null}
      {stroke.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
